/**
 * Pure reconciliation matching engine (Phase 4).
 *
 * Given a BankTxn + the open journal lines on the corresponding clearing
 * account, score candidate matches by amount equality, date proximity, and
 * memo similarity. No state, no I/O, no posting — just suggestions.
 */
import type { JournalEntry } from "../glTypes";
import type { BankAccount, BankTxn, MatchSuggestion } from "./bankFeedTypes";

const dayMs = 24 * 60 * 60 * 1000;
const CENT = (n: number) => Math.round(n * 100);

/** Lightweight token overlap; not perfect but good for memo cues. */
function memoSimilarity(a: string, b: string): number {
  const norm = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9 ]/g, " ").split(/\s+/).filter(t => t.length >= 3);
  const ta = new Set(norm(a));
  const tb = new Set(norm(b));
  if (ta.size === 0 || tb.size === 0) return 0;
  let hit = 0;
  ta.forEach(t => { if (tb.has(t)) hit++; });
  return hit / Math.max(ta.size, tb.size);
}

/**
 * Returns ranked match suggestions for one BankTxn.
 * Looks at JEs that posted to the clearing account on the matching side
 * (a customer payment hits Unreconciled Cash as DR → bank inflow = CR match).
 */
export function suggestMatches(
  txn: BankTxn,
  account: BankAccount,
  openEntries: JournalEntry[],
  limit = 5,
): MatchSuggestion[] {
  const suggestions: MatchSuggestion[] = [];
  const txnTime = new Date(txn.postedDate).getTime();
  // Opposite side on the clearing account = what we need to wipe out.
  // Inflow on bank means clearing was DR (we held the cash pending bank); match DR.
  // Outflow on bank means clearing was CR; match CR. For CC payable, inverse.
  const expectClearingSide: "debit" | "credit" =
    account.kind === "bank"
      ? (txn.side === "inflow" ? "debit" : "credit")
      : (txn.side === "outflow" ? "debit" : "credit");

  for (const je of openEntries) {
    if (je.status !== "posted") continue;
    for (const line of je.lines) {
      if (line.accountId !== account.clearingAccountId) continue;
      if (line.side !== expectClearingSide) continue;
      if (CENT(line.amount) !== CENT(txn.amount)) continue;

      const dDays = Math.abs(new Date(je.postingDate).getTime() - txnTime) / dayMs;
      if (dDays > 14) continue;

      const reasons: string[] = [`Amount matches ($${txn.amount.toFixed(2)})`];
      let score = 60; // amount + side equality baseline

      if (dDays <= 1) { score += 25; reasons.push("Same-day posting"); }
      else if (dDays <= 3) { score += 15; reasons.push(`Within ${Math.round(dDays)}d`); }
      else if (dDays <= 7) { score += 8; reasons.push(`Within ${Math.round(dDays)}d`); }
      else { reasons.push(`${Math.round(dDays)}d apart`); }

      const memoText = [je.description, line.memo].filter(Boolean).join(" ");
      const sim = memoSimilarity(txn.description, memoText);
      if (sim >= 0.5) { score += 15; reasons.push("Memo strongly matches"); }
      else if (sim >= 0.2) { score += 7; reasons.push("Memo partially matches"); }

      suggestions.push({
        bankTxnId: txn.id,
        journalEntryId: je.id,
        score: Math.min(100, score),
        reasons,
      });
    }
  }

  return suggestions.sort((a, b) => b.score - a.score).slice(0, limit);
}

export interface ReconciliationSummary {
  glAccountId: string;
  unmatchedCount: number;
  unmatchedInflow: number;
  unmatchedOutflow: number;
  matchedCount: number;
  lastImportAt?: string;
}

export function summarize(
  account: BankAccount,
  txns: BankTxn[],
): ReconciliationSummary {
  const scoped = txns.filter(t => t.glAccountId === account.glAccountId);
  let inflow = 0, outflow = 0, matched = 0, unmatched = 0;
  let last: string | undefined;
  for (const t of scoped) {
    if (!last || t.importedAt > last) last = t.importedAt;
    if (t.status === "matched") matched++;
    else if (t.status === "unmatched") {
      unmatched++;
      if (t.side === "inflow") inflow += t.amount; else outflow += t.amount;
    }
  }
  return {
    glAccountId: account.glAccountId,
    unmatchedCount: unmatched,
    unmatchedInflow: inflow,
    unmatchedOutflow: outflow,
    matchedCount: matched,
    lastImportAt: last,
  };
}
